import React from 'react'
import { flushSync } from 'react-dom'
import ReactDOM from 'react-dom/client'
import { QRCode } from 'react-qr-code'

import { NETWORK_COLOR_MAP, NetworkIcon } from '@root/entities/network/ui'
import { Trend } from '@root/entities/trend'
import { NumericFormat } from '@root/shared/core/format'
import { environmentAtom } from '@root/shared/environment'

import PNL_BG_NEGATIVE_PNG from './assets/pnl-position-negative-bg.png'
import PNL_BG_POSITIVE_PNG from './assets/pnl-position-positive-bg.png'
import {
  CanvasPnlBuilderTemplate,
  CANVAS_RENDER_SCALE,
} from './canvas-pnl-builder-template'
import { renderAvatarOrText } from './canvas-utils'

import type { Theme } from '@blade/ui-kit'
import type { Ctx } from '@reatom/framework'
import type { Pair } from '@root/entities/pair'
import type {
  UnrealizedPnlNative,
  UnrealizedPnlUsd,
  WalletPositionModel,
} from '@root/entities/position'
import type { ReferralCode } from '@root/entities/referral/model/referral-code'
import type { UserShort } from '@root/entities/user-short'
import type BigNumber from 'bignumber.js'

type TransKeys =
  | 'common.total-invested'
  | 'common.total-sold'
  | 'common.total-profit'

const BG_WIDTH = 417 * CANVAS_RENDER_SCALE
const BG_RATIO = 1.23

export class CanvasPnlPositionBuilder extends CanvasPnlBuilderTemplate {
  public readonly position: WalletPositionModel
  public readonly pnlNative: UnrealizedPnlNative
  public readonly pnlUsd: UnrealizedPnlUsd
  public readonly pair: Pair.PairMinimal
  private user: UserShort.UserShort
  private referralCode: ReferralCode | undefined
  private translations: Record<TransKeys, string>
  private theme: Theme
  private downloadUrl: string

  constructor(
    position: WalletPositionModel,
    user: UserShort.UserShort,
    referralCode: ReferralCode | undefined,
    translations: Record<string, string>,
    reatomCtx: Ctx,
    pair: Pair.PairMinimal,
    theme: Theme,
  ) {
    const pnlNative = reatomCtx.get(position.totalPnlNative)
    const pnlUsd = reatomCtx.get(position.totalPnlUsd)

    super(
      pnlUsd.pnlPercentage.gte(0),
      reatomCtx,
      PNL_BG_POSITIVE_PNG,
      PNL_BG_NEGATIVE_PNG,
      BG_WIDTH,
      BG_RATIO,
    )

    this.downloadUrl = reatomCtx.get(environmentAtom).downloadUrl
    this.position = position
    this.user = user
    this.referralCode = referralCode
    this.translations = translations
    this.pnlNative = pnlNative
    this.pnlUsd = pnlUsd
    this.theme = theme
    this.pair = pair
  }

  protected renderAll = async () => {
    // Header
    this.renderCurrency()
    await this.renderTotalsNative()
    this.renderTotalsPercent()

    // Body
    this.renderTotalsSummary()

    // Footer
    this.renderUser(
      58,
      404,
      this.user.username,
      this.user.firstName,
      this.user.lastName,
      this.user.avatarSrc,
    )

    await this.renderQrCode()
  }

  private renderQrCode = async () => {
    // If no referral code, don't render QR code
    if (!this.referralCode) {
      return
    }

    this.ctx.save()
    try {
      // Background
      this.ctx.beginPath()
      this.ctx.roundRect(this.x(290), this.x(375), this.x(70), this.x(70), [
        this.x(9),
      ])
      this.ctx.fillStyle = '#FFF'
      this.ctx.fill()

      this.ctx.beginPath()
      this.ctx.rect(this.x(290), this.x(398), this.x(70), this.x(70))
      this.ctx.fillStyle = 'rgba(255, 255, 255, 0.1)'
      this.ctx.fill()

      // QR Code
      const element = React.createElement(QRCode, {
        // TODO: Replace with referral code from Deeplink URL when available
        value: this.downloadUrl,
        size: 112,
      })
      const svgString = await this.renderReactToString(element)
      const svgBase64 = window.btoa(unescape(encodeURIComponent(svgString)))
      const image64 = `data:image/svg+xml;base64,${svgBase64}`

      this.image(297, 382, 0, image64)

      // Text
      this.ctx.font = '20px Inter_Bold'
      this.ctx.textAlign = 'center'
      this.ctx.fillStyle = '#FFF'
      this.ctx.fillText(
        this.referralCode.toUpperCase(),
        this.x(325),
        this.x(461),
      )
    } finally {
      this.ctx.restore()
    }
  }

  private renderCurrency = () => {
    this.ctx.save()
    try {
      renderAvatarOrText(
        this.ctx,
        262,
        128,
        43,
        this.theme.palette[NETWORK_COLOR_MAP[this.pair.quote.network]][9],
        this.pair.quote.ticker,
        this.reatomCtx.get(this.pair.quote.icon),
      )
      this.ctx.textAlign = 'left'

      // Ticker
      this.ctx.textAlign = 'left'
      this.ctx.font = '28px Inter_Bold'
      this.ctx.fillStyle = '#fff'
      this.ctx.fillText(this.pair.quote.ticker, this.x(67), this.x(142))
    } finally {
      this.ctx.restore()
    }
  }

  private renderTotalsNative = async () => {
    draw(this.ctx, () => {
      this.measureAndRenderPrice(
        this.pnlUsd.pnlUsd,
        65,
        176,
        {
          size: 48,
          fillStyle: this.getPnlColor(),
          align: 'left',
        },
        {
          preset: 'currency-amount-short',
          forceSign: true,
          currency: '$',
        },
      )
    })
  }

  private renderNativeIcon = (x: number, y: number, color: string) => {
    draw(this.ctx, () => {
      const element = NetworkIcon({
        network: this.pair.quote.network,
        color,
      })
      const svgString = this.renderReactToString(element)
      const svgBase64 = window.btoa(svgString)
      const image64 = `data:image/svg+xml;base64,${svgBase64}`
      this.image(x, y, 7, image64)
    })
  }

  private renderTotalsPercent = () => {
    const startX = 65
    const containerHeight = 26
    const containerPadding = 5

    const renderContainer = ({
      x,
      y,
      textWidth,
    }: {
      x: number
      y: number
      textWidth: number
    }) => {
      draw(this.ctx, () => {
        this.ctx.beginPath()
        this.ctx.roundRect(
          this.x(x),
          this.x(y),
          this.x(textWidth + containerPadding * 2),
          this.x(containerHeight),
          [6 * CANVAS_RENDER_SCALE],
        )
        this.ctx.fillStyle = 'rgba(255, 255, 255, 0.07)'
        this.ctx.fill()
      })
    }

    this.ctx.save()
    try {
      this.ctx.font = '33px Inter_SemiBold'

      // Calculate text width
      const textWidth = this.measureAndRenderPrice(
        this.pnlUsd.pnlPercentage,
        startX + 5,
        210,
        {
          size: 33,
          fillStyle: 'transparent',
          align: 'left',
        },
        {
          currency: '',
          preset: 'currency-amount-short',
          forceSign: true,
        },
      )

      const percentSignWidth =
        this.ctx.measureText('%').width / CANVAS_RENDER_SCALE

      // Background
      renderContainer({
        x: startX,
        y: 192,
        textWidth: textWidth + percentSignWidth,
      })

      // Percent value
      this.ctx.fillStyle = this.getPnlColor()
      const percentX = this.measureAndRenderPrice(
        this.pnlUsd.pnlPercentage,
        startX + 5,
        210,
        {
          size: 33,
          fillStyle: this.getPnlColor(),
          align: 'left',
        },
        {
          currency: '',
          preset: 'currency-amount-short',
          forceSign: true,
        },
      )

      // Percent sign
      this.ctx.fillText(
        '%',
        this.x(startX + percentX + containerPadding),
        this.x(210),
      )

      // native pnl

      const nativeContainerStartX =
        startX + textWidth + containerPadding * 2 + percentSignWidth + 4

      const nativeTrend = Trend.of(this.pnlNative.pnlNative)
      const renderNativePnl = () => {
        const textWidth = this.measureAndRenderPrice(
          this.pnlNative.pnlNative,
          nativeContainerStartX + containerPadding,
          210,
          {
            size: 33,
            fillStyle: this.getTrendColor(nativeTrend),
            align: 'left',
          },
          {
            currency: '',
            preset: 'currency-amount-short',
            forceSign: true,
          },
        )

        return textWidth
      }

      const nativeTextWidth = renderNativePnl()

      this.renderNativeIcon(
        nativeContainerStartX + containerPadding + nativeTextWidth + 2,
        198,
        this.getTrendColor(nativeTrend),
      )

      renderContainer({
        x: nativeContainerStartX,
        y: 192,
        textWidth: nativeTextWidth + 17,
      })
    } finally {
      this.ctx.restore()
    }
  }

  private renderUser = (
    x: number,
    y: number,
    username: string,
    firstName?: string,
    lastName?: string,
    avatarSrc?: string,
  ) => {
    this.ctx.save()
    try {
      const initials = `${firstName ?? username}${lastName || ''}`
        .slice(0, 2)
        .toUpperCase()

      renderAvatarOrText(this.ctx, x, y, 24, '#ffffff33', initials, avatarSrc)

      this.ctx.textAlign = 'left'
      this.ctx.fillStyle = '#FFF'
      this.ctx.font = '32px Inter_SemiBold'

      const xName = x + 58
      const fullName = lastName
        ? `${firstName ?? username} ${lastName}`
        : (firstName ?? username)

      this.ctx.fillText(fullName, this.x(xName), this.x(y + 18))
      this.renderDate(xName, 444, new Date())
    } finally {
      this.ctx.restore()
    }
  }

  private renderTotalsSummary = () => {
    this.ctx.save()
    try {
      this.ctx.font = '32px Inter_Medium'
      this.ctx.fillStyle = '#fff'
      this.ctx.fillText(
        this.translations['common.total-invested'],
        this.x(57),
        this.y(300),
      )
      this.ctx.fillText(
        this.translations['common.total-sold'],
        this.x(57),
        this.y(340),
      )

      // Total invested
      this.ctx.font = '32px Inter_SemiBold'
      this.ctx.fillStyle = '#fff'
      this.ctx.textAlign = 'left'
      const xInvested = this.measureAndRenderPrice(
        this.reatomCtx.get(this.position.boughtSizeUsd),
        360,
        300,
        {
          size: 32,
          fillStyle: '#fff',
          align: 'right',
        },
        {
          currency: '$',
          preset: 'currency-amount-short',
          forceSign: false,
        },
      )
      this.renderSeparatorLineFromPx(
        300,
        this.getTextWidthPx(this.translations['common.total-invested']) + 5,
        this.x(xInvested) + 5,
      )

      // Total sold
      const xSold = this.measureAndRenderPrice(
        this.reatomCtx.get(this.position.soldSizeUsd),
        360,
        340,
        {
          size: 32,
          fillStyle: '#fff',
          align: 'right',
        },
        {
          currency: '$',
          preset: 'currency-amount-short',
          forceSign: false,
        },
      )
      this.renderSeparatorLineFromPx(
        340,
        this.getTextWidthPx(this.translations['common.total-sold']) + 5,
        this.x(xSold) + 5,
      )
    } finally {
      this.ctx.restore()
    }
  }

  private getTextWidthPx = (text: string): number =>
    this.ctx.measureText(text).width

  private renderSeparatorLineFromPx = (
    y: number,
    pxLeft: number,
    pxRight: number,
  ) => {
    this.ctx.save()
    try {
      this.ctx.fillStyle = 'rgba(255, 255, 255, 0.15)'
      this.ctx.font = '32px Inter_Medium'

      const a = this.x(57 + pxLeft / 2)
      const b = this.x(360 - pxRight / 2)
      const width = b - a
      const line = '.'.repeat(width / 8)

      this.ctx.fillText(line, this.x(57) + pxLeft, this.x(y), width)
    } finally {
      this.ctx.restore()
    }
  }

  private measureAndRenderPrice = (
    number: BigNumber | number,
    x: number,
    y: number,
    textStyle: {
      size: number
      fillStyle: string
      align: 'left' | 'right'
    },
    numberFormat: {
      currency?: string
      preset: NumericFormat.Preset
      forceSign: boolean
    },
  ): number => {
    this.ctx.save()
    try {
      const { size, fillStyle, align } = textStyle
      const { currency, preset, forceSign } = numberFormat

      const render = (x: number, fillStyle: string) => {
        const parts = NumericFormat.formatPresetToParts(number, preset, {
          currency,
          forceSign,
        })

        this.ctx.fillStyle = fillStyle

        let xx = x

        for (const part of Object.values(parts)) {
          if (part.type === 'compact-decimal-zeroes') {
            this.ctx.font = `${size / 1.8}px Inter_Bold`
            this.ctx.fillText(part.value, this.x(xx), this.x(y + 4))
          } else {
            this.ctx.font = `${size}px Inter_Bold`

            this.ctx.fillText(part.value, this.x(xx), this.x(y))
          }

          xx += this.ctx.measureText(part.value).width / 2

          // extra space for minus sign or plus sign
          if (part.type === 'minusSign' || part.type === 'plusSign') {
            this.ctx.fillText(' ', this.x(xx), this.x(y))
            xx += this.ctx.measureText(' ').width / 2
          }
        }

        return xx - x
      }

      if (align === 'right') {
        const xx = render(x, 'transparent')

        return render(x - xx, fillStyle)
      }

      return render(x, fillStyle)
    } finally {
      this.ctx.restore()
    }
  }

  private renderReactToString(element: React.ReactNode): string {
    const container = document.createElement('div')
    const root = ReactDOM.createRoot(container)

    flushSync(() => {
      root.render(element)
    })

    const htmlString = container.innerHTML
    root.unmount() // cleanup
    return htmlString
  }
}

const draw = (ctx: CanvasRenderingContext2D, fn: () => void) => {
  ctx.save()
  try {
    fn()
  } finally {
    ctx.restore()
  }
}
